import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";

/**
 * Fires exactly one GA4 page_view per client-side navigation.
 *
 * - The initial mount counts as the first page_view (gtag's built-in
 *   send_page_view is disabled in index.html, so there is no duplicate).
 * - Subsequent route changes trigger a new page_view.
 * - Admin routes are excluded — the CMS is not part of the public site.
 * - Because some routes are React.lazy() chunks, `document.title` may not
 *   be up-to-date at the moment the effect runs (the child PageLayout
 *   hasn't mounted yet while Suspense shows its fallback). To handle both
 *   eager AND lazy pages correctly, we set up a MutationObserver on the
 *   <title> element: as soon as PageLayout updates the title we fire the
 *   page_view. A safety timeout at 800ms guarantees we still fire even if
 *   no title change is observed (e.g. legal pages that reuse the same
 *   title as the previous route).
 * - `useRef` dedupes the same key across React 18 StrictMode's dev-only
 *   double-invocation.
 */
export default function AnalyticsListener() {
  const { pathname, search } = useLocation();
  const lastTrackedRef = useRef(null);

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    const key = pathname + search;
    if (lastTrackedRef.current === key) return;

    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      lastTrackedRef.current = key;
      trackPageView({ path: key, title: document.title });
    };

    // Watch <title> for the update that PageLayout writes after mount.
    const titleEl = document.querySelector("title");
    let observer;
    if (titleEl && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        fire();
        if (observer) observer.disconnect();
      });
      observer.observe(titleEl, { childList: true, subtree: true, characterData: true });
    }
    // Safety fallback if the title happens not to change on this navigation.
    const t = setTimeout(fire, 800);

    return () => {
      clearTimeout(t);
      if (observer) observer.disconnect();
    };
  }, [pathname, search]);

  return null;
}
